import React, { useEffect, useState } from "react";
import { api } from "../api";

const AdminSmartPro = () => {
  const [states, setStates] = useState([]);
  const [stateCode, setStateCode] = useState("");
  const [questions, setQuestions] = useState([]);
  const [hoaQuestions, setHoaQuestions] = useState([]);
  const [stateDocuments, setStateDocuments] = useState([]);
  const [csv, setCsv] = useState(null);
  const [hoaCsv, setHoaCsv] = useState(null);
  const [docId, setDocId] = useState("");
  const [stateDocFile, setStateDocFile] = useState(null);
  const [stateDocTitle, setStateDocTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [indexingAll, setIndexingAll] = useState(false);

  const loadStates = async () => {
    try {
      const response = await api.get("/api/smart/admin/states");
      setStates(response.data);
    } catch (error) {
      console.error("Error loading states:", error);
      setMessage("Error loading states");
    }
  };

  const loadQuestions = async (code) => {
    if (!code) {
      setQuestions([]);
      return;
    }
    
    try {
      // Load general state questions (apply to all states)
      const generalResponse = await api.get("/api/smart/admin/questions", { 
        params: { scope: "state", general: "true" } 
      });
      const generalQuestions = generalResponse.data || [];
      
      // Load state-specific questions
      const specificResponse = await api.get("/api/smart/admin/questions", { 
        params: { state_code: code } 
      });
      const specificQuestions = specificResponse.data || [];
      
      // Combine: general + state-specific
      const combined = [...generalQuestions, ...specificQuestions];
      setQuestions(combined);
    } catch (error) {
      console.error("Error loading questions:", error);
      setMessage("Error loading questions");
    }
  };
  
  const loadHoaQuestions = async () => {
    try {
      const response = await api.get("/api/smart/admin/questions", { 
        params: { scope: "hoa" } 
      });
      setHoaQuestions(response.data);
    } catch (error) {
      console.error("Error loading HOA questions:", error);
      setMessage("Error loading HOA questions");
    }
  };
  
  const loadStateDocuments = async (code) => {
    if (!code) {
      setStateDocuments([]);
      return;
    }
    try {
      const response = await api.get("/api/smart/admin/state-documents", {
        params: { state_code: code }
      });
      setStateDocuments(response.data);
    } catch (error) {
      console.error("Error loading state documents:", error);
      setMessage("Error loading state documents");
    }
  };

  useEffect(() => { 
    loadStates();
    loadHoaQuestions(); 
  }, []);

  useEffect(() => { 
    if (stateCode) {
      loadQuestions(stateCode);
      loadStateDocuments(stateCode);
    } else {
      setStateDocuments([]);
    }
  }, [stateCode]);

  const importCsv = async () => {
    if (!csv) {
      setMessage("Please select a file");
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", csv);
      
      const response = await api.post("/api/smart/admin/questions/import", formData);
      setMessage(`Successfully imported ${response.data.upserted} questions`);
      
      if (stateCode) loadQuestions(stateCode);
    } catch (error) {
      console.error("Error importing file:", error);
      setMessage(error.response?.data?.detail || "Error importing file");
    }
    setLoading(false);
  };

  const addStateDoc = async () => {
    if (!stateCode) {
      setMessage("State code is required");
      return;
    }
    
    if (!stateDocFile && !docId) {
      setMessage("Please upload a file or provide a Document ID");
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("state_code", stateCode);
      formData.append("title", stateDocTitle || `${stateCode} Statute`);
      
      if (stateDocFile) {
        formData.append("file", stateDocFile);
      } else if (docId) {
        formData.append("document_id", docId);
      }
      
      await api.post("/api/smart/admin/state-doc/upload", formData);
      setMessage("State document uploaded and indexed successfully");
      setDocId("");
      setStateDocFile(null);
      setStateDocTitle("");
      loadStates(); // Refresh states list
    } catch (error) {
      console.error("Error uploading state document:", error);
      setMessage(error.response?.data?.detail || "Error uploading state document");
    }
    setLoading(false);
  };

  const exportQuestions = async (format = 'csv') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (stateCode) params.append('state_code', stateCode);
      params.append('format', format);
      
      const response = await api.get(`/api/smart/admin/questions/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Determine proper extension and mime type
      const extension = format === 'xlsx' || format === 'excel' ? 'xlsx' : format === 'json' ? 'json' : 'csv';
      const mimeType = format === 'xlsx' || format === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : format === 'json' 
        ? 'application/json'
        : 'text/csv';
      
      // Create blob with proper mime type
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `smart_pro_questions_${stateCode || 'all'}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMessage(`Successfully exported ${questions.length} questions as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Error exporting questions:", error);
      setMessage("Error exporting questions");
    }
    setLoading(false);
  };
  
  const exportHoaQuestions = async (format = 'csv') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('scope', 'hoa');
      params.append('format', format);
      
      const response = await api.get(`/api/smart/admin/questions/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Determine proper extension and mime type
      const extension = format === 'xlsx' || format === 'excel' ? 'xlsx' : format === 'json' ? 'json' : 'csv';
      const mimeType = format === 'xlsx' || format === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : format === 'json' 
        ? 'application/json'
        : 'text/csv';
      
      // Create blob with proper mime type
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hoa_questions.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMessage(`Successfully exported ${hoaQuestions.length} HOA questions as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Error exporting HOA questions:", error);
      setMessage("Error exporting HOA questions");
    }
    setLoading(false);
  };
  
  const importHoaCsv = async () => {
    if (!hoaCsv) {
      setMessage("Please select a file");
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", hoaCsv);
      
      const response = await api.post("/api/smart/admin/questions/import", formData);
      setMessage(`Successfully imported ${response.data.upserted} HOA questions`);
      loadHoaQuestions();
    } catch (error) {
      console.error("Error importing file:", error);
      setMessage(error.response?.data?.detail || "Error importing file");
    }
    setLoading(false);
  };
  
  const deleteStateDoc = async (docId, title) => {
    if (!confirm(`Delete "${title}"? This will remove it from the state documents list.`)) {
      return;
    }
    
    setLoading(true);
    try {
      await api.delete(`/api/smart/admin/state-doc/${docId}`);
      setMessage(`Successfully deleted "${title}"`);
      loadStateDocuments(stateCode); // Refresh list
    } catch (error) {
      console.error("Error deleting state document:", error);
      setMessage(error.response?.data?.detail || "Error deleting document");
    }
    setLoading(false);
  };

  const reindexAllHoas = async () => {
    if (!confirm("This will process all uploaded documents for ALL HOAs and create search indexes. This may take several minutes. Continue?")) {
      return;
    }
    
    setIndexingAll(true);
    setMessage("Starting global reindex... This may take several minutes.");
    
    try {
      const response = await api.post("/api/smart/admin/reindex-all-hoas");
      setMessage(`✅ Success! Processed ${response.data.processed_documents} documents across ${response.data.hoas_indexed} HOAs. ${response.data.failed_documents} failed.`);
    } catch (error) {
      console.error("Error reindexing all HOAs:", error);
      setMessage(error.response?.data?.detail || "Error reindexing HOAs");
    }
    setIndexingAll(false);
  };

  const reindex = async () => {
    if (!stateCode) {
      setMessage("Please select a state");
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/api/smart/admin/reindex/state/${stateCode}`);
      setMessage("State documents reindexed successfully");
    } catch (error) {
      console.error("Error reindexing:", error);
      setMessage("Error reindexing state documents");
    }
    setLoading(false);
  };

  return (
    <div className="p-4 space-y-6">
      <h1 className="text-xl font-semibold">HOA Smart Pro – Admin</h1>
      
      {message && (
        <div className={`p-3 rounded ${
          message.includes("Error") ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
        }`}>
          {message}
        </div>
      )}

      {/* Global Reindex Button */}
      <div className="rounded-xl border p-4 bg-gradient-to-r from-orange-50 to-yellow-50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg mb-1">🔄 Global Document Indexing</h2>
            <p className="text-sm text-gray-600">
              Process all uploaded governing documents for all HOAs to make them searchable in Smart Pro
            </p>
          </div>
          <button 
            onClick={reindexAllHoas}
            disabled={indexingAll}
            className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed font-semibold whitespace-nowrap"
          >
            {indexingAll ? "⏳ Processing..." : "🚀 Reindex All HOAs"}
          </button>
        </div>
      </div>

      {/* HOA Questions Management */}
      <div className="rounded-xl border p-4 bg-blue-50">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">📋 HOA Question Bank (My HOA Documents)</h2>
          <div className="flex gap-2 items-center flex-wrap">
            <button 
              onClick={() => exportHoaQuestions('csv')}
              disabled={loading || hoaQuestions.length === 0}
              className="px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              📥 Export CSV
            </button>
            <button 
              onClick={() => exportHoaQuestions('xlsx')}
              disabled={loading || hoaQuestions.length === 0}
              className="px-3 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              📥 Export Excel
            </button>
            <button 
              onClick={() => exportHoaQuestions('json')}
              disabled={loading || hoaQuestions.length === 0}
              className="px-3 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              📥 Export JSON
            </button>
          </div>
        </div>
        
        <div className="text-sm text-gray-600 mb-3">
          File formats supported: CSV, Excel (.xlsx), JSON
          <br />
          <strong>CSV format:</strong> id, scope, state_code, category, question, keywords, section_filters, regex_extracts, answer_template, enabled
          <br />
          <strong>Note:</strong> For HOA questions, set scope="hoa" and leave state_code empty
        </div>
        
        <div className="bg-white rounded-lg p-4 border mb-3">
          <div className="font-medium text-sm mb-2">📤 Upload Questions</div>
          <div className="flex items-center gap-3">
            <input 
              type="file" 
              accept=".csv,.xlsx,.json" 
              onChange={(e) => setHoaCsv(e.target.files[0])}
              className="flex-1 p-2 border rounded-lg text-sm"
            />
            <button 
              onClick={importHoaCsv}
              disabled={loading || !hoaCsv}
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap font-medium"
            >
              {loading ? "⏳ Uploading..." : "📤 Upload File"}
            </button>
          </div>
          {hoaCsv && (
            <div className="text-sm text-gray-600 mt-2">
              Selected: {hoaCsv.name}
            </div>
          )}
        </div>
        
        {hoaQuestions.length > 0 && (
          <div className="max-h-60 overflow-y-auto bg-white rounded p-2">
            <div className="text-sm font-semibold mb-2">
              {hoaQuestions.length} HOA Questions Loaded
            </div>
            <ul className="text-sm space-y-1">
              {hoaQuestions.map(q => (
                <li key={q.id} className="p-2 border-b">
                  <div className="font-medium">{q.category}: {q.question}</div>
                  <div className="text-gray-500 text-xs">
                    Keywords: {q.keywords?.join(", ") || "None"} | 
                    {q.enabled ? " ✅ Enabled" : " ❌ Disabled"}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {hoaQuestions.length === 0 && (
          <div className="text-gray-500 text-sm bg-white rounded p-3">
            No HOA questions found. Import a CSV file to add questions for the "My HOA Documents" section.
          </div>
        )}
      </div>

      <div className="rounded-xl border p-4">
        <h2 className="font-semibold mb-3">State Documents Management</h2>
        <div className="mb-4">
          <label className="text-sm font-medium block mb-1">State</label>
          <select 
            value={stateCode} 
            onChange={(e) => setStateCode(e.target.value)}
            className="w-full p-2 border rounded-lg"
          >
            <option value="">Select a state</option>
            {states.map(s => (
              <option key={s.state_code} value={s.state_code}>
                {s.state_code} ({s.docs} docs)
              </option>
            ))}
            {/* Add US states if not in states list */}
            {["AL","AK","AZ","AR","CA","CO","CT","DC","DE","FL","GA","HI","IA","ID","IL","IN","KS","KY","LA","MA","MD","ME","MI","MN","MO","MS","MT","NC","ND","NE","NH","NJ","NM","NV","NY","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VA","VT","WA","WI","WV","WY"].filter(code => 
              !states.find(s => s.state_code === code)
            ).map(code => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
        </div>
        
        <div className="space-y-3 mb-3 p-3 bg-gray-50 rounded-lg">
          <div>
            <label className="text-sm font-medium block mb-1">Document Title</label>
            <input 
              type="text"
              value={stateDocTitle} 
              onChange={(e) => setStateDocTitle(e.target.value)} 
              placeholder={`${stateCode || 'State'} Statute Document`}
              className="w-full p-2 border rounded-lg"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium block mb-2">Upload New File (PDF, DOC, DOCX)</label>
            <input 
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={(e) => {
                setStateDocFile(e.target.files[0]);
                setDocId(""); // Clear doc ID if file is selected
              }}
              className="w-full p-2 border rounded-lg text-sm"
            />
          </div>
          
          <div className="text-center text-sm text-gray-500 py-2">
            — OR —
          </div>
          
          <div>
            <label className="text-sm font-medium block mb-1">Existing Document ID</label>
            <input 
              type="text"
              value={docId} 
              onChange={(e) => {
                setDocId(e.target.value);
                setStateDocFile(null); // Clear file if doc ID is entered
              }}
              placeholder="Enter document ID with chunks"
              className="w-full p-2 border rounded-lg"
            />
          </div>
        </div>
        
        <div className="flex gap-2">
          <button 
            onClick={addStateDoc}
            disabled={loading || !stateCode || (!stateDocFile && !docId)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Uploading..." : stateDocFile ? "Upload Document" : "Link Document"}
          </button>
          <button 
            onClick={reindex}
            disabled={loading || !stateCode}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Rebuilding..." : "Rebuild Index"}
          </button>
        </div>
      </div>

      {/* State Documents List */}
      {stateCode && (
        <div className="rounded-xl border p-4 bg-gray-50">
          <h2 className="font-semibold mb-3">📁 Uploaded Documents for {stateCode}</h2>
          {stateDocuments.length > 0 ? (
            <div className="space-y-2">
              {stateDocuments.map((doc, idx) => (
                <div key={idx} className="p-3 bg-white rounded-lg border flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-sm">{doc.title}</div>
                    <div className="text-xs text-gray-500">
                      Document ID: {doc.document_id} | 
                      Uploaded: {new Date(doc.created_at).toLocaleDateString()} |
                      {doc.chunks_count ? ` ${doc.chunks_count} chunks` : ' Not processed'}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => window.open(`/api/documents/${doc.document_id}/view`, '_blank')}
                      className="px-3 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                    >
                      📄 View
                    </button>
                    <button 
                      onClick={() => deleteStateDoc(doc.id, doc.title)}
                      disabled={loading}
                      className="px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600 disabled:opacity-50"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-500 text-sm p-3 bg-white rounded">
              No documents uploaded for {stateCode} yet.
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">State Question Bank</h2>
          <div className="flex gap-2 items-center flex-wrap">
            <button 
              onClick={() => exportQuestions('csv')}
              disabled={loading || questions.length === 0}
              className="px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              📥 Export CSV
            </button>
            <button 
              onClick={() => exportQuestions('xlsx')}
              disabled={loading || questions.length === 0}
              className="px-3 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              📥 Export Excel
            </button>
            <button 
              onClick={() => exportQuestions('json')}
              disabled={loading || questions.length === 0}
              className="px-3 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              📥 Export JSON
            </button>
          </div>
        </div>
        
        <div className="text-sm text-gray-600 mb-3">
          File formats supported: CSV, Excel (.xlsx), JSON
          <br />
          <strong>CSV format:</strong> id, scope, state_code, category, question, keywords, section_filters, regex_extracts, answer_template, enabled
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 border mb-3">
          <div className="font-medium text-sm mb-2">📤 Upload State Questions</div>
          <div className="flex items-center gap-3">
            <input 
              type="file" 
              accept=".csv,.xlsx,.json" 
              onChange={(e) => setCsv(e.target.files[0])}
              className="flex-1 p-2 border rounded-lg text-sm bg-white"
            />
            <button 
              onClick={importCsv}
              disabled={loading || !csv}
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap font-medium"
            >
              {loading ? "⏳ Uploading..." : "📤 Upload File"}
            </button>
          </div>
          {csv && (
            <div className="text-sm text-gray-600 mt-2">
              Selected: {csv.name}
            </div>
          )}
        </div>
        
        {questions.length > 0 && (
          <div className="max-h-60 overflow-y-auto">
            <ul className="text-sm space-y-1">
              {questions.map(q => (
                <li key={q.id} className="p-2 border-b">
                  <div className="font-medium">{q.category}: {q.question}</div>
                  <div className="text-gray-500 text-xs">
                    Keywords: {q.keywords?.join(", ") || "None"} | 
                    {q.enabled ? " Enabled" : " Disabled"}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {questions.length === 0 && stateCode && (
          <div className="text-gray-500 text-sm">No questions found for {stateCode}</div>
        )}
      </div>

      <div className="rounded-xl border p-4">
        <h2 className="font-semibold mb-3">Sample CSV Download</h2>
        <p className="text-sm text-gray-600 mb-3">
          Download a sample CSV file with example questions to get started:
        </p>
        <button 
          onClick={() => {
            const csvContent = `id,scope,state_code,category,question,keywords,section_filters,regex_extracts,answer_template,enabled
state_notice_days_CA,state,CA,notice,"How many days of notice are required for board meetings in California?","notice;meeting;days;mail;email","notice;meeting","[{""name"":""min_days"",""pattern"":""(?:at least|no less than)\\\\s+(\\\\d{1,3})\\\\s+days"",""flags"":""i""}]","State requires at least {{min_days}} days' notice for board meetings.",true
hoa_arc_fence,hoa,,arc,"What do our CCRs say about building a fence?","fence;arc;architectural;improvement;setback;height","fence;architectural",,,true
hoa_pets,hoa,,rules,"What are the pet rules (number, size, leash, nuisance)?","pet;animal;leash;nuisance;waste","pet;animal",,,true`;
            
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'smart_pro_questions_sample.csv';
            a.click();
            window.URL.revokeObjectURL(url);
          }}
          className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
        >
          Download Sample CSV
        </button>
      </div>
    </div>
  );
};

export default AdminSmartPro;